import React, { memo } from 'react';
import PropTypes from 'prop-types';
import SelectDaysButtonsCell from './SelectDaysButtonsCell';
import dayjs from '../../config/dayjs';
import { DEFAULT_CALENDAR_MIN_DAYS_FROM_TODAY } from '../../constants';
import { useRowDateToPayState } from '../../hooks';

/**
 * SelectDaysButtonsSelectionStateManagerCell
 *
 * This component handles the state management for date selection
 * so that general buttons (30/60/90 days) are automatically
 * deselected when a specific date is selected in the row.
 *
 * Functionality:
 * - When a general button (30/60/90 days) is selected, it resets the row state
 *   to allow the selection of general buttons
 * - When a specific date is selected, the state is marked as modified
 *   and passed to SelectDaysButtonsCell as unselectButtons=true
 * - This makes SelectDaysButtons deselect the general buttons
 *   and only show the specifically selected date
 */

const SelectDaysButtonsSelectionStateManagerCell = memo(({
  params,
  selectedDaysButtonsOnChange,
  selectedDaysButtonsOnRecommendedPaymentTerm,
  daysButtons,
  isMobile,
  sameDateForAllSelection,
  minDaysFromToday,
}) => {
  const { isSingleRowModified, setSingleRowModified } = useRowDateToPayState(params.row.id);

  const handleDaysChange = (newValue) => {
    setSingleRowModified(false);
    selectedDaysButtonsOnChange(newValue, params.row);
  };

  const handleRecommendedTerm = (newValue) => {
    setSingleRowModified(false);
    selectedDaysButtonsOnRecommendedPaymentTerm(newValue, params.row);
  };

  return (
    <SelectDaysButtonsCell
      params={params}
      selectedDaysButtonsOnChange={handleDaysChange}
      selectedDaysButtonsOnRecommendedPaymentTerm={handleRecommendedTerm}
      daysButtons={daysButtons}
      isMobile={isMobile}
      unselectButtons={isSingleRowModified}
      sameDateForAllSelection={sameDateForAllSelection}
      minDaysFromToday={minDaysFromToday}
    />
  );
});

SelectDaysButtonsSelectionStateManagerCell.propTypes = {
  params: PropTypes.shape({
    row: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  selectedDaysButtonsOnChange: PropTypes.func.isRequired,
  selectedDaysButtonsOnRecommendedPaymentTerm: PropTypes.func.isRequired,
  daysButtons: PropTypes.arrayOf(PropTypes.shape({
    days: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  })).isRequired,
  isMobile: PropTypes.bool,
  sameDateForAllSelection: PropTypes.instanceOf(dayjs),
  minDaysFromToday: PropTypes.number,
};

SelectDaysButtonsSelectionStateManagerCell.defaultProps = {
  isMobile: false,
  sameDateForAllSelection: null,
  minDaysFromToday: DEFAULT_CALENDAR_MIN_DAYS_FROM_TODAY,
};

export default SelectDaysButtonsSelectionStateManagerCell;
